import type {
  ActivityMessage,
  AssistantMessage,
  Message,
  UserMessage,
} from '@ag-ui/core';
import { useRenderActivityMessage } from '@copilotkit/react-core/v2';
import type {
  CopilotChatAssistantMessageProps,
  CopilotChatUserMessageProps,
} from '@copilotkit/react-core/v2';
import { Box } from '@mui/material';

import { InkAssistantMessageSlot } from '@/components/shared/AgentAssistantPopup/slots/InkAssistantMessageSlot';
import { InkUserMessageSlot } from '@/components/shared/AgentAssistantPopup/slots/InkUserMessageSlot';
import { MessageEnter } from '@/components/shared/AgentAssistantPopup/styles';
import { assistantContentDomId } from '@/components/shared/AgentAssistantPopup/utils';

export type MessageItemProps = {
  message: Message;
  messages: Message[];
  isRunning: boolean;
  isEntering?: boolean;
  isCurrentTurn?: boolean;
  onThumbsUp?: (message: AssistantMessage) => void;
  onThumbsDown?: (
    message: AssistantMessage,
    values: {
      issueType: string;
      comment: string;
    },
  ) => void;

  onCopy?: (content: string, messageIds: string[]) => void;
  messageIds?: string[];
};

export const MessageItem = ({
  message,
  messages,
  isRunning,
  isEntering,
  isCurrentTurn,
  onThumbsUp,
  onThumbsDown,
  onCopy,
  messageIds,
}: MessageItemProps) => {
  const { renderActivityMessage } = useRenderActivityMessage();

  if (message.role === 'user') {
    const props: CopilotChatUserMessageProps = {
      message: message as UserMessage,
    };
    return (
      <MessageEnter isEntering={isEntering} variant="user">
        <InkUserMessageSlot {...props} />
      </MessageEnter>
    );
  }

  if (message.role === 'assistant') {
    const assistant = message as AssistantMessage;
    const hasContent = (assistant.content ?? '').trim() !== '';
    if (!hasContent) return null;

    const props: CopilotChatAssistantMessageProps = {
      message: assistant,
      messages,
      isRunning,
    };
    return (
      <MessageEnter isEntering={isEntering} variant="ai">
        <Box id={assistantContentDomId(assistant.id)}>
          <InkAssistantMessageSlot
            {...props}
            isCurrentTurn={isCurrentTurn}
            onThumbsUp={onThumbsUp}
            onThumbsDown={onThumbsDown}
            onCopy={onCopy}
            messageIds={messageIds}
          />
        </Box>
      </MessageEnter>
    );
  }

  // Reasoning messages are intentionally not rendered. The backend streams them
  // but they are not meant to be shown; `MessageList` shows the thinking
  // indicator while they stream instead.
  if (message.role === 'reasoning') return null;

  if (message.role === 'activity') {
    return (
      <MessageEnter isEntering={isEntering} variant="ai">
        {renderActivityMessage(message as ActivityMessage)}
      </MessageEnter>
    );
  }
  return null;
};
